import { Box, Button, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import ApyInfo from '@ui/components/ApyInfo';
import Balance from '@ui/components/Balance';
import Info from '@ui/components/Info';
import { InputCard } from '@ui/components/InputCard';
import { RenderInMobile } from '@ui/components/MobileOrDesktop';
import { MonthlyRewardChart } from '@ui/components/MonthlyRewardChart';
import StakingInfoDrawer from '@ui/components/StakingInfoDrawer';
import { useTransakModal } from '@ui/components/TransakModal';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useTranslations } from '@ui/i18n';
import Link from '@ui/overrides/next/link';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import { formatNumber } from '@ui/utils/format';
import getBgImage from '@ui/utils/getBgImageProps';
import type { NextPage } from 'next';
import numbro from 'numbro';
import { useEffect, useMemo, useState } from 'react';

import useStakeViewData from '../../hooks/useStakeViewData';
import StakeCroDrawer from './StakeCroDrawer';

export const StakeView: NextPage = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const [isDisplayTotalExact, setIsDisplayTotalExact] = useState(false);
  const {
    isConnected,
    apy,
    apyLoading,
    account,
    apyList,
    balance,
    balanceUsd,
    croPerStcro,
    grossApy,
    maxStakeAmount,
  } = useStakeViewData();
  const isDesktop = useBreakpointValue({
    base: false,
    desktop: true,
  });
  const maxDecimalsAllowed = Math.min(coinModeInfo.decimals, 8);

  const [stakingInfoDrawerOpen, setStakingInfoDrawerOpen] = useState(false);
  const [amountToStake, setAmountToStake] = useState('');
  useEffect(() => {
    setAmountToStake('');
    setInputError('');
  }, [globalCoinMode]);
  const { data: oneYearUsdRewards } = useOneYearUsdRewards(
    apyList,
    parseFloat(amountToStake ?? 0),
  );
  const { data: dollarToStake } = useCoinUsdValue(
    parseFloat(amountToStake ?? 0),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputError, setInputError] = useState('');

  const numberToStake = useMemo(
    () => parseFloat(amountToStake),
    [amountToStake],
  );

  const bgImageProps =
    globalCoinMode === 'atom'
      ? getBgImage('PATTERN_1_ATOM')
      : getBgImage('PATTERN_2');

  const openTransakModal = useTransakModal((s) => s.onOpen);

  return (
    <>
      <RenderInMobile>
        <StakingInfoDrawer
          netApy={`${(apy * 100).toFixed(2)}%`}
          grossApy={`${((grossApy ?? 0) * 100).toFixed(2)}%`}
          isOpen={stakingInfoDrawerOpen}
          close={() => setStakingInfoDrawerOpen(false)}
        />
      </RenderInMobile>
      <WithWallet
        mt="32px"
        mb="24px"
        px="16px"
        connectHint={t('Connect your wallet to stake {coin}', {
          coin: coinModeInfo.name,
        })}
        bgPos="right -36px top -50px"
        minH="572px"
        {...bgImageProps}
      >
        <StakeCroDrawer
          coinMode={globalCoinMode}
          isDrawerOpen={drawerOpen}
          onCloseDrawer={() => {
            setDrawerOpen(false);
            setAmountToStake('');
          }}
          amountToStake={amountToStake}
          apyList={apyList}
          balance={balance ?? 0}
          balanceUsd={balanceUsd ?? 0}
          croPerStcro={croPerStcro ?? 1}
          account={account ?? ''}
          oneYearUsdRewards={oneYearUsdRewards}
        />
        <Balance
          title={t('Wallet {coin} Balance', {
            coin: coinModeInfo.name,
          })}
          amount={balance}
          icon={<coinModeInfo.Icon />}
          usdAmount={balanceUsd}
          usdDisplayDecimals={2}
          showUsdAmount
        />
        <Flex mt="4px" mb="36px" justify="center">
          {globalCoinMode === 'atom' ? (
            <Button
              as="a"
              variant="link"
              color="text-link"
              href={EXTERNAL_LINK.vvsSwapCroToAtom}
              target="_blank"
            >
              {t('Buy {coin}', {
                coin: 'ATOM',
              })}
            </Button>
          ) : (
            <Button
              variant="link"
              color="text-link"
              onClick={() => {
                openTransakModal({
                  entryPoint: 'Stake',
                });
              }}
            >
              {t('Buy {coin}', {
                coin: 'CRO',
              })}
            </Button>
          )}
        </Flex>
        <Box as="main">
          <Info
            infoButtonAction={() =>
              !isDesktop && setStakingInfoDrawerOpen(true)
            }
            tooltip={
              apy && grossApy ? (
                <ApyInfo
                  netApy={`${(apy * 100).toFixed(2)}%`}
                  grossApy={`${(grossApy * 100).toFixed(2)}%`}
                />
              ) : undefined
            }
            tooltipProps={{ minW: { base: 'full', desktop: '407px' } }}
            title={t('Real Staking APY')}
            value={
              !apyLoading
                ? apy === 0
                  ? '--'
                  : `${(apy * 100).toFixed(2)}%`
                : undefined
            }
          />
          {!isConnected ? (
            <Flex width="full" alignItems="center">
              Please <Link href="/">connect your wallet</Link> first.
            </Flex>
          ) : (
            <Flex justify="flex-start" flexDir={'column'} gap={4} mt="12px">
              <InputCard
                label={t('Input {coin} amount', {
                  coin: coinModeInfo.name,
                })}
                max={maxStakeAmount ? maxStakeAmount : undefined}
                warningMessage={
                  amountToStake === String(maxStakeAmount) &&
                  coinModeInfo.coinMode === 'cro'
                    ? t('Gas fee has been deducted from maximum balance')
                    : undefined
                }
                errorMessage={inputError}
                value={amountToStake}
                maxDecimals={maxDecimalsAllowed}
                usdValue={dollarToStake}
                onChange={(value) => {
                  // maximum of 8 decimals are allowed by the smart contract
                  if (
                    value &&
                    !new RegExp(
                      `^[0-9]+\\.?[0-9]{0,${maxDecimalsAllowed}}$`,
                    ).test(value)
                  ) {
                    if (
                      new RegExp(`\\.?[0-9]{${maxDecimalsAllowed + 1},}$`).test(
                        value,
                      )
                    ) {
                      setInputError(
                        t('maximum {decimal} decimal places allowed', {
                          decimal: maxDecimalsAllowed,
                        }),
                      );
                    } else {
                      setInputError(t('invalid format'));
                    }
                  } else if (parseFloat(value) < coinModeInfo.minStakeAmount) {
                    setInputError(
                      t('Below minimum limit {minAmount} {coin}', {
                        minAmount: coinModeInfo.minStakeAmount,
                        coin: coinModeInfo.name,
                      }),
                    );
                  } else if (parseFloat(value) > (balance ?? 0)) {
                    setInputError(
                      t('{coin} balance too low', {
                        coin: coinModeInfo.name,
                      }),
                    );
                  } else {
                    setInputError('');
                  }
                  setAmountToStake(value);
                }}
              />
              <Button
                mt={1}
                mb={2}
                onClick={() => setDrawerOpen(true)}
                disabled={!!inputError || !amountToStake}
                w={{ desktop: MAXW_DESKTOP_CTA }}
                mx={{ desktop: 'auto' }}
              >
                {t('Stake')}
              </Button>
              <Info
                title={
                  <Box>
                    <Text>{t('You will receive')}</Text>
                    <Button
                      display="block"
                      variant="link"
                      onClick={() => setIsDisplayTotalExact((v) => !v)}
                    >
                      {t('Show exact')}
                    </Button>
                  </Box>
                }
                value={
                  !amountToStake || isNaN(numberToStake) || numberToStake <= 0
                    ? `0 ${coinModeInfo.liquidName}`
                    : croPerStcro
                    ? isDisplayTotalExact
                      ? `${numbro(numberToStake / croPerStcro).format({
                          thousandSeparated: true,
                          mantissa: coinModeInfo.liquidDecimals,
                          trimMantissa: true,
                        })} ${coinModeInfo.liquidName}`
                      : `${numbro(
                          Math.floor((100 * numberToStake) / croPerStcro) / 100,
                        ).format({
                          thousandSeparated: true,
                          mantissa: 2,
                        })} ${coinModeInfo.liquidName}`
                    : undefined
                }
                mt="4px"
              />
              <Info
                title={t('Exchange rate')}
                value={
                  croPerStcro
                    ? `1 ${coinModeInfo.liquidName} = ${croPerStcro.toFixed(
                        4,
                      )} ${coinModeInfo.name}`
                    : undefined
                }
              />
              <Info
                title={t('Estimated rewards')}
                convertedValue={
                  // don't show if there's an input error, else value will shown as 0 and conv. value > 0
                  !isNaN(oneYearUsdRewards ?? 0) && !inputError
                    ? `${formatNumber(oneYearUsdRewards ?? 0, 2)} USD`
                    : undefined
                }
                value={
                  apyList.length < 3 || !Number(amountToStake) || inputError ? (
                    <>
                      <Text as="span" whiteSpace="nowrap">
                        0.00 {coinModeInfo.name}
                      </Text>
                      <Text as="span" whiteSpace="nowrap">
                        {' '}
                        {t('in 1 year')}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text as="span" whiteSpace="nowrap">
                        {(Number(amountToStake) * apyList[2]).toFixed(2)}{' '}
                        {coinModeInfo.name}
                      </Text>{' '}
                      <Text as="span" whiteSpace="nowrap">
                        {' '}
                        {t('in 1 year')}
                      </Text>
                    </>
                  )
                }
              />
              <MonthlyRewardChart
                coinMode={globalCoinMode}
                amount={Number(amountToStake)}
                warningMessage={
                  !Number(amountToStake)
                    ? t('Input {coin} amount above to see estimated rewards', {
                        coin: coinModeInfo.name,
                      })
                    : undefined
                }
                errorMessage={
                  inputError
                    ? t('Estimated rewards isn’t available at the moment')
                    : undefined
                }
              />
            </Flex>
          )}
        </Box>
      </WithWallet>
    </>
  );
};

const useOneYearUsdRewards = (apyList: number[], amount: number) => {
  const oneYearCroRewards = apyList.length > 2 ? apyList[2] * amount : 0;
  return useCoinUsdValue(oneYearCroRewards);
};
